import { useMemo } from "react";

import {
  useCreateBillingPortalSession,
  useCreateCheckoutSession,
  useSubscriptionPlans,
} from "$/api/payment";
import { Button } from "$/lib/components";
import CheckIcon from "$/lib/icons/CheckIcon";
import SuccessCircleIcon from "$/lib/icons/SuccessCircleIcon";
import { SuccessIcon } from "$/lib/icons/SuccessIcon";
import { useAuth } from "$/lib/providers/AuthProvider";
import {
  PlanIntervalEnum,
  SubscriptionStatusEnum,
} from "$/lib/types/models/Subscription.types";
import { Analytics } from "$/lib/utils/analytics";

const PaymentPlansList = () => {
  const { data: subPlans, isLoading } = useSubscriptionPlans();
  const { mutate: createBillingPortalSession, isPending } =
    useCreateBillingPortalSession();
  const { mutate: createCheckoutSession, isPending: isCheckoutPending } =
    useCreateCheckoutSession();

  const { user } = useAuth();
  const hasSubscription =
    Boolean(user?.subscription) &&
    user?.subscription.status !== SubscriptionStatusEnum.CANCELED &&
    user?.subscription.status !== SubscriptionStatusEnum.CANCELLING;

  const plans = useMemo(
    () =>
      (subPlans ?? []).map((plan) => ({
        id: plan.id,
        name: plan.name,
        price: (plan.unitAmount / 100).toString(),
        period: plan.interval === PlanIntervalEnum.MONTH ? "/mois" : "/an",
        originalPrice:
          plan.interval === PlanIntervalEnum.YEAR
            ? `€${((plan.unitAmount / 1000) * 12).toFixed(0)}`
            : undefined,
        badge:
          plan.interval === PlanIntervalEnum.YEAR
            ? "Économisez 2 mois !"
            : undefined,
        stripePriceId: plan.stripePriceId,
      })),
    [subPlans],
  );

  if (isLoading) {
    return <div>Chargement des formules...</div>;
  }

  const handleOpenBillingPortal = () => {
    createBillingPortalSession(
      { returnUrl: window.location.href, purpose: "payment_method_management" },
      {
        onSuccess: (res) => {
          if (res?.url) {
            window.location.href = res.url;
          }
        },
      },
    );
  };

  const handleCreateCheckoutSession = (priceId: string) => {
    createCheckoutSession(
      {
        priceId,
        successUrl: window.location.href,
        cancelUrl: window.location.href,
      },
      {
        onSuccess: (res) => {
          if (res?.url) {
            window.location.href = res.url;
          }
        },
      },
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 screen850:grid-cols-2">
      {plans.map((plan) => {
        const isCurrentPlan =
          hasSubscription &&
          plan.stripePriceId === user?.subscription?.stripePriceId;

        return (
          <div
            key={plan.id}
            className={`relative rounded-2xl border-2 px-5 py-7 transition-all duration-200 ${
              isCurrentPlan
                ? "border-secondary-300"
                : "hover:border-2 hover:border-primary hover:bg-primary-50 hover:shadow-md"
            }`}
          >
            {/* Badge économie */}
            {plan.badge && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-2 py-1.5 text-xs font-medium text-white shadow-md">
                {plan.badge}
              </div>
            )}

            {isCurrentPlan && (
              <div className="absolute -right-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-secondary-300 shadow-md">
                <SuccessCircleIcon className="h-6 w-6 text-secondary-300" />
              </div>
            )}
            {/* Prix */}
            <div className="mb-12 pt-2 text-center">
              <h3 className="mb-8 text-xl font-bold text-textColor">
                {plan.name}
              </h3>
              <div className="flex items-baseline justify-center gap-2">
                {plan.originalPrice && (
                  <span className="text-sm text-[#696680] line-through">
                    {plan.originalPrice}
                  </span>
                )}
                <span className="text-[26px] font-bold text-textColor">
                  {plan.price}
                </span>
                <span className="text-lg text-[#696680]">€{plan.period}</span>
              </div>
              {/* ✅ AJOUT : Ligne de conversion */}
              {plan.period === "/mois" && (
                <>
                  <p className="mt-1 text-xs font-medium text-textInfo">
                    (soit {(parseInt(plan.price) * 12).toFixed(0)} euros par an)
                  </p>
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <CheckIcon className="h-3 w-3 text-[#00D9A5]" />
                    <span className="text-xs font-medium text-textColor">
                      Déductible en frais professionnels
                    </span>
                  </div>
                </>
              )}
              {plan.period === "/an" && (
                <>
                  <p className="mt-1 text-xs font-medium text-textInfo">
                    (soit {(parseInt(plan.price) / 12).toFixed(0)} euros par
                    mois)
                  </p>
                  <div className="mt-2 flex items-center justify-center gap-1.5">
                    <CheckIcon className="h-3 w-3 text-[#00D9A5]" />
                    <span className="text-xs font-medium text-textColor">
                      Déductible en frais professionnels
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* CTA */}
            {isCurrentPlan ? (
              <Button
                theme="secondary"
                className="w-full !bg-info !px-4"
                disabled
              >
                <span className="text-sm font-semibold text-textInfo">
                  Formule actuelle
                </span>
              </Button>
            ) : (
              <Button
                theme="primary"
                className="w-full"
                onClick={() => {
                  plan.period === "/mois"
                    ? Analytics.cta.click({
                        page: "app_payment",
                        event_category: "conversion",
                        cta_text: "formule_mensuelle",
                      })
                    : Analytics.cta.click({
                        page: "app_payment",
                        event_category: "conversion",
                        cta_text: "formule_annuelle",
                      });
                  handleCreateCheckoutSession(plan.stripePriceId);
                }}
                disabled={hasSubscription || isCheckoutPending}
              >
                <span className="text-sm font-medium">
                  {hasSubscription
                    ? "Résilier pour changer de formule"
                    : "Choisir cette formule"}
                </span>
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PaymentPlansList;
